import React, {
  useContext,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react'
import type { DragEvent } from 'react'
import numeral from 'numeral'
import Uppy from '@uppy/core'
import { DashboardModal } from '@uppy/react'
import type { UppyFile } from '@uppy/core'
import Webcam from '@uppy/webcam'
import GoogleDrive from '@uppy/google-drive'
import Dropbox from '@uppy/dropbox'
// import Facebook from '@uppy/facebook'
// import Instagram from '@uppy/instagram'
import Box from '@uppy/box';
import OneDrive from '@uppy/onedrive';
import Unsplash from '@uppy/unsplash';
import Url from '@uppy/url'
import ImageKitUppyPlugin from 'imagekit-uppy-plugin'
import { UserContext, MLContext, UserProvider } from '../../../providers'
import { addTrailingSlash } from '../../../../utils/urlHelpers'
import * as ReactDOM from 'react-dom'

import {
  metaFields,
  fileConstraints,
  hasPermission,
  addIncrementInFileNameIfRequired,
  getFolderPathForFile,
  getAllFileEntries,
  getFileFromFileEntry,
  getFileVersionFolderPath,
  validateCustomMetadata,
  formatCustomMetadata,
} from './uploadUtils'
import '@uppy/core/dist/style.css'
import '@uppy/dashboard/dist/style.css'
import '@uppy/webcam/dist/style.css'
// import '@uppy/url/dist/style.css'
import { isRestrictedUser } from '../../../../utils/roles'
import { ACLPermissionEnum } from '../../../constants'
import { useTranslation } from 'react-i18next'
import { FileDetailsResponse } from '../../../interfaces/FileDetailsResponse'
import useToast from '../../../hooks/useToast'
import EditMetadata from './EditMetaData'
import AdvancePopup from './AdvancePopup'
import { FormikProps, FormikValues } from 'formik'
import { ChakraProvider, extendTheme, Portal } from '@chakra-ui/react'
import { CustomMetadataContext } from '../../../providers/CustomMetadataProvider'
import { checkAllowedPostTransformation, convertDefaultUploadParameters } from '../../../../utils/mediaLibrary'
import {
  CommonSettingProps,
  Extensions,
  UppyFileType,
} from '../../../interfaces/SettingsResponse'
import { theme } from '../../../../ui-kit'
import { isNull, isUndefined } from 'lodash'
interface FileWithRelativePath extends File {
  webkitRelativePath: string
}
interface Props {
  isModalOpen: boolean
  openModal: Function
  closeModal: Function
  setOnFilesDropFunction?: Function
  setOnFileOrDirectoryDropFunction?: Function
  fileVersion?: FileDetailsResponse
}

interface CustomFileEvent extends CustomEvent {
  detail: {
    file?: FileWithRelativePath
  }
}

const FileUpload = ({
  isModalOpen,
  openModal,
  closeModal,
  setOnFilesDropFunction,
  setOnFileOrDirectoryDropFunction,
  fileVersion,
}: Props): React.ReactElement => {
  const { user } = useContext(UserContext)
  const [{ folderPath, folderPermission, refetch, currentMediaCollection }] =
    useContext(MLContext)
  const [isOpen, setIsOpen] = React.useState(false)
  const shouldRefetch = useRef(false)
  const formRef = useRef<FormikProps<FormikValues>>(null)
  const [commonSettings, setCommonSettings] =
    React.useState<CommonSettingProps>({})
  const { cmFields } = useContext(CustomMetadataContext)

  const refetchFnRef = useRef(refetch)

  const { t } = useTranslation()

  const toast = useToast()

  const uppy = useMemo(() => {
    const uppyObj = new Uppy({
      meta: {
        folder: fileVersion
          ? getFileVersionFolderPath(fileVersion.filePath)
          : folderPath,
      },
      restrictions: {
        maxNumberOfFiles: fileConstraints.maxNumberOfFiles,
      },
      onBeforeFileAdded: (currentFile, files) => {
        // if(!hasPermission(PermissionsEnum.SELF_MEDIA_LIBRARY_MANAGE, user?.permissions)) return false;

        // no of files check
        if (fileVersion && Object.values(files).length > 0) {
          uppy.info(
            t(
              'uppy_file_version_limit',
              'Only one file can be added as a new version at a time.',
            ),
            'error',
            5000,
          )
          return false
        }
        if (Object.values(files).length === fileConstraints.maxNumberOfFiles) {
          uppy.info(
            `Max ${fileConstraints.maxNumberOfFiles} files allowed`,
            'error',
            5000,
          )
          return false
        }

        let userUploadLimits = fileConstraints.maxSize

        if (user?.pricingPlan.limits.fixed.uploadSize) {
          userUploadLimits = user.pricingPlan.limits.fixed.uploadSize
        }

        if (currentFile.name.startsWith('.')) {
          return false // silently remove hidden files
        }

        const currentFileType = currentFile.type ?? ''

        // file type and its corresponding max size check
        if (fileConstraints.allowedFiles.image.includes(currentFileType)) {
          // image constraint
          if (currentFile.data.size > userUploadLimits.image) {
            uppy.info(
              `Max image size allowed is ${numeral(userUploadLimits.image)
                .format('0.0 ib')
                .replace('i', '')}`,
              'error',
              5000,
            )
            return false
          }
        } else if (
          fileConstraints.allowedFiles.video.includes(currentFileType)
        ) {
          // video constraint
          if (currentFile.data.size > userUploadLimits.video) {
            uppy.info(
              `Max video size allowed is ${numeral(userUploadLimits.video)
                .format('0.0 ib')
                .replace('i', '')}`,
              'error',
              5000,
            )
            return false
          }
        } else if (
          fileConstraints.allowedFiles.audio.includes(currentFileType)
        ) {
          // audio constraint
          if (currentFile.data.size > userUploadLimits.audio) {
            uppy.info(
              `Max audio size allowed is ${numeral(userUploadLimits.audio)
                .format('0.0 ib')
                .replace('i', '')}`,
              'error',
              5000,
            )
            return false
          }
        } else {
          // raw constraint
          if (currentFile.data.size > userUploadLimits.raw) {
            uppy.info(
              `Max file size allowed is ${numeral(userUploadLimits.raw)
                .format('0.0 ib')
                .replace('i', '')}`,
              'error',
              5000,
            )
            return false
          }
        } 

        if (fileVersion) {
          const modifiedFile = {
            ...currentFile,
            name: fileVersion.name,
            meta: {
              ...currentFile.meta,
              name: fileVersion.name,
            },
          }
          return modifiedFile
        }
        // add increment if file added is of same name
        const modifiedFileName = addIncrementInFileNameIfRequired(
          currentFile,
          files,
        )
        if (modifiedFileName !== currentFile.name) {
          const modifiedFile = {
            ...currentFile,
            name: modifiedFileName,
            meta: {
              ...currentFile.meta,
              name: modifiedFileName,
            },
          }
          return modifiedFile
        }

        return true
      },
      onBeforeUpload: (files) => {
        const currentUppyFiles=Object.keys(files)?.map(key=>files[key])
        const commonSettingsButton = document.getElementById('upload-button')
        if (commonSettingsButton)
          commonSettingsButton.style.visibility = 'hidden'

        // updating meta data of all files with common settings
        currentUppyFiles.forEach((file: UppyFileType) => {
          const commonSettings = file.meta
            .IK_TEMP_COMMON_SETTINGS as CommonSettingProps
          if (!isUndefined(file?.meta?.tags) && isNull(file?.meta?.tags)) {
            const metaOb = file.meta
            delete metaOb.tags
            file.meta = metaOb
          } else if (
            commonSettings?.tags &&
            Array.isArray(commonSettings.tags) &&
            !file.meta.tags
          ) {
            file.meta.tags = commonSettings?.tags
              ?.map(data => data.label)
              .join(',')
          }

          if (
            file?.type &&
            fileConstraints.allowedFiles.image.includes(file.type)
          ) {
            if (
              commonSettings &&
              commonSettings?.customCoordinatesOb?.['x-coordinate'] &&
              commonSettings?.customCoordinatesOb?.['y-coordinate'] &&
              commonSettings?.customCoordinatesOb?.height &&
              commonSettings?.customCoordinatesOb.width &&
              !file?.meta.customCoordinates &&
              !(
                !isUndefined(file?.meta.customCoordinates) &&
                isNull(file?.meta.customCoordinates)
              )
            )
              file.meta.customCoordinates = `${commonSettings?.customCoordinatesOb['x-coordinate']},${commonSettings?.customCoordinatesOb['y-coordinate']},${commonSettings?.customCoordinatesOb.width},${commonSettings?.customCoordinatesOb?.height}`

            if (
              commonSettings &&
              commonSettings?.extensionsData &&
              !file?.meta?.extensions &&
              !(
                !isUndefined(file?.meta.extensions) &&
                isNull(file?.meta.extensions)
              )
            ) {
              let tempExt: Extensions[] = []
              if (
                commonSettings?.extensionsData?.aiTag &&
                commonSettings?.aiTagData
              ) {
                tempExt = [...commonSettings.aiTagData]
              }
              if (commonSettings?.extensionsData?.removeBg) {
                const options = {
                  add_shadow: commonSettings?.removeBgData?.shadowCheck,
                  semitransparency:
                    commonSettings?.removeBgData?.semiTransparentCheck,
                }
                if (
                  commonSettings?.removeBgData?.background ===
                    'background_color' &&
                  commonSettings?.removeBgData?.bgColor
                )
                  options['bg_color'] = commonSettings?.removeBgData?.bgColor
                if (
                  commonSettings?.removeBgData?.background ===
                    'background_image' &&
                  commonSettings?.removeBgData?.bgImageURL
                )
                  options['bg_image_url'] =
                    commonSettings?.removeBgData?.bgImageURL
                tempExt.push({
                  name: 'remove-bg',
                  options,
                })
              }
              if (tempExt?.length)
                file.meta.extensions = JSON.stringify(tempExt)
            }
          }

          if (
            !isUndefined(file?.meta?.webhookUrl) &&
            isNull(file?.meta?.webhookUrl)
          ) {
            const metaOb = file.meta
            delete metaOb.webhookUrl
            file.meta = metaOb
          } else if (
            commonSettings &&
            commonSettings?.webhookUrl &&
            !file?.meta?.webhookUrl
          )
            file.meta.webhookUrl = commonSettings?.webhookUrl
          if (
            commonSettings &&
            Object.keys(commonSettings).includes('useUniqueFileName') &&
            file?.meta &&
            !Object.keys(file?.meta).includes('useUniqueFileName')
          )
            file.meta.useUniqueFileName = commonSettings?.useUniqueFileName
          if (
            commonSettings &&
            Object.keys(commonSettings).includes('overwriteFile') &&
            file?.meta &&
            !Object.keys(file?.meta).includes('overwriteFile')
          )
            file.meta.overwriteFile = commonSettings?.overwriteFile
          if (
            commonSettings &&
            Object.keys(commonSettings).includes('isPrivateFile') &&
            file?.meta &&
            !Object.keys(file?.meta).includes('isPrivateFile')
          )
            file.meta.isPrivateFile = commonSettings?.isPrivateFile
          if (
            commonSettings &&
            Object.keys(commonSettings).includes('isPublished') &&
            file?.meta &&
            !Object.keys(file?.meta).includes('isPublished')
          ) {
            file.meta.isPublished = commonSettings?.isPublished
          }
          if (
            commonSettings &&
            Object.keys(commonSettings).includes('overwriteAITags') &&
            file?.meta &&
            !Object.keys(file?.meta).includes('overwriteAITags')
          )
            file.meta.overwriteAITags = commonSettings?.overwriteAITags
          if (
            commonSettings &&
            Object.keys(commonSettings).includes('overwriteTags') &&
            file?.meta &&
            !Object.keys(file?.meta).includes('overwriteTags')
          )
            file.meta.overwriteTags = commonSettings?.overwriteTags
          if (
            commonSettings &&
            Object.keys(commonSettings).includes('overwriteCustomMetadata') &&
            file?.meta &&
            !Object.keys(file?.meta).includes('overwriteCustomMetadata')
          )
            file.meta.overwriteCustomMetadata =
              commonSettings?.overwriteCustomMetadata
          if (
            file.type &&
            (fileConstraints.allowedFiles.image.includes(file.type) ||
              fileConstraints.allowedFiles.video.includes(file.type))
          ) {
            if (
              commonSettings &&
              Object.keys(commonSettings).includes('preTransformation') &&
              commonSettings.preTransformation !== '' &&
              file?.meta &&
              !Object.keys(file?.meta).includes('preTransformation')
            ) {
              file.meta.preTransformation = commonSettings?.preTransformation
            }
            if (
              commonSettings &&
              Object.keys(commonSettings).includes('postTransformation') &&
              !isNull(commonSettings?.postTransformation) &&
              file?.meta &&
              !Object.keys(file?.meta).includes('postTransformation')
            ) {
              file.meta.postTransformation = checkAllowedPostTransformation(
                commonSettings?.postTransformation,
                file.type,
              )
            }

            if (
              file?.meta?.preTransformation &&
              file?.meta?.postTransformation
            ) {
              file.meta.transformation = JSON.stringify({
                pre: file.meta.preTransformation,
                post: file.meta.postTransformation,
              })
            } else if (file?.meta?.preTransformation) {
              file.meta.transformation = JSON.stringify({
                pre: file.meta.preTransformation,
              })
            } else if (file?.meta?.postTransformation) {
              file.meta.transformation = JSON.stringify({
                post: file.meta.postTransformation,
              })
            }
          }

          if (
            commonSettings &&
            Object.keys(commonSettings).includes('customMetadataSelect') &&
            file?.meta &&
            !Object.keys(file?.meta).includes('customMetadataSelect')
          )
            file.meta.customMetadataSelect =
              commonSettings?.customMetadataSelect

        })
        // update global parameters
        const defaultmetaData = convertDefaultUploadParameters(user)
        // update each file default metadata
        currentUppyFiles.forEach(uppyFileObj => {
          const globalParameters = {}
          const commonSettings = uppyFileObj.meta
            .IK_TEMP_COMMON_SETTINGS as CommonSettingProps
          if (
            uppyFileObj?.meta &&
            !Object.keys(uppyFileObj?.meta).includes('isPrivateFile')
          )
            globalParameters['isPrivateFile'] = defaultmetaData?.isPrivateFile
          if (
            uppyFileObj?.meta &&
            !Object.keys(uppyFileObj?.meta).includes('isPublished')
          ) {
            globalParameters['isPublished'] = defaultmetaData?.isPublished
          }
          if (
            uppyFileObj?.type &&
            fileConstraints.allowedFiles.image.includes(uppyFileObj.type)
          ) {
            if (
              !isUndefined(uppyFileObj?.meta?.customCoordinates) &&
              isNull(uppyFileObj?.meta?.customCoordinates)
            ) {
              const metaOb = uppyFileObj.meta
              delete metaOb.customCoordinates
              uppyFileObj.meta = metaOb
            } else if (
              uppyFileObj?.meta &&
              !Object.keys(uppyFileObj?.meta).includes('customCoordinates')
            )
              globalParameters['customCoordinates'] =
                defaultmetaData?.customCoordinates

            if (
              !isUndefined(uppyFileObj?.meta?.extensions) &&
              isNull(uppyFileObj?.meta?.extensions)
            ) {
              const metaOb = uppyFileObj.meta
              delete metaOb.extensions
              uppyFileObj.meta = metaOb
            } else if (
              uppyFileObj?.meta &&
              !Object.keys(uppyFileObj?.meta).includes('extensions')
            )
              globalParameters['extensions'] = JSON.stringify(
                defaultmetaData?.extensions,
              )
          }

          if (fileVersion) globalParameters['useUniqueFileName'] = 'false'
          else if (
            uppyFileObj?.meta &&
            !Object.keys(uppyFileObj?.meta).includes('useUniqueFileName')
          )
            globalParameters['useUniqueFileName'] =
              defaultmetaData?.useUniqueFileName

          if (fileVersion) globalParameters['overwriteFile'] = 'true'
          else if (
            uppyFileObj?.meta &&
            !Object.keys(uppyFileObj?.meta).includes('overwriteFile')
          )
            globalParameters['overwriteFile'] = defaultmetaData?.overwriteFile
          if (
            uppyFileObj?.meta &&
            !Object.keys(uppyFileObj?.meta).includes('overwriteTags')
          )
            globalParameters['overwriteTags'] = defaultmetaData?.overwriteTags
          if (
            uppyFileObj?.meta &&
            !Object.keys(uppyFileObj?.meta).includes('overwriteAITags')
          )
            globalParameters['overwriteAITags'] =
              defaultmetaData?.overwriteAITags
          if (
            uppyFileObj?.meta &&
            !Object.keys(uppyFileObj?.meta).includes('overwriteCustomMetadata')
          ) {
            globalParameters['overwriteCustomMetadata'] =
            defaultmetaData?.overwriteCustomMetadata
            uppyFileObj.meta.overwriteCustomMetadata = defaultmetaData?.overwriteCustomMetadata
          }

          if (
            uppyFileObj?.meta &&
            !Object.keys(uppyFileObj?.meta).includes('preTransformation')
          ) {
            globalParameters['preTransformation'] =
              defaultmetaData?.preTransformation
          }

          if (
            uppyFileObj?.meta &&
            !Object.keys(uppyFileObj?.meta).includes('postTransformation')
          ) {
            globalParameters['postTransformation'] =
              defaultmetaData?.postTransformation
          }

          if (
            user?.customMetadata &&
            Array.isArray(user?.customMetadata) &&
            cmFields?.length
          ) {
            globalParameters['customMetadata'] = user?.customMetadata?.reduce(
              (ob, data) => ({ ...ob, ...data }),
              {},
            )
          }

          // updated custom metadata
          if (cmFields?.length) {
            let fileObject = {}
            cmFields.forEach(cmData => {
              if (validateCustomMetadata(uppyFileObj?.meta, cmData?.name))
                fileObject = formatCustomMetadata(
                  fileObject,
                  uppyFileObj?.meta,
                  cmData?.name,
                  cmData?.name,
                )
              else if (
                !(
                  uppyFileObj?.meta?.customMetadata &&
                  typeof uppyFileObj?.meta.customMetadata === 'string' &&
                  Object.keys(JSON.parse(uppyFileObj?.meta.customMetadata))
                    .length &&
                  isNull(
                    JSON.parse(uppyFileObj?.meta?.customMetadata)[cmData?.name],
                  )
                )
              ) {
                if (validateCustomMetadata(commonSettings, cmData?.name))
                  fileObject = formatCustomMetadata(
                    fileObject,
                    commonSettings,
                    cmData?.name,
                    cmData?.name,
                  )
                else if (
                  user?.customMetadata &&
                  Array.isArray(user.customMetadata) &&
                  Object.keys(globalParameters['customMetadata'])?.length &&
                  !(
                    isUndefined(
                      globalParameters['customMetadata'][cmData?.name],
                    ) ||
                    isNull(globalParameters['customMetadata'][cmData?.name])
                  )
                )
                  fileObject = {
                    ...fileObject,
                    [cmData?.name]:
                      globalParameters['customMetadata'][cmData?.name],
                  }
              }
              if(!cmData.schema.isValueRequired && !isUndefined(cmData.schema.defaultValue)
                && !Object.keys(fileObject).includes(cmData.name) )
                {
                  fileObject = {
                    ...fileObject,
                    [cmData?.name]: cmData.schema.defaultValue 
                  }
                }

            })


            globalParameters['customMetadata'] = JSON.stringify(fileObject)
          }

          if (
            uppyFileObj.type &&
            (fileConstraints.allowedFiles.image.includes(uppyFileObj.type) ||
              fileConstraints.allowedFiles.video.includes(uppyFileObj.type))
          ) {
            let transformation
            if (
              uppyFileObj.meta &&
              !Object.keys(uppyFileObj?.meta).includes('transformation')
            ) {
              transformation = uppyFileObj.meta.transformation
            }
            if (globalParameters['preTransformation']) {
              globalParameters['transformation'] = JSON.stringify({
                ...transformation,
                pre: globalParameters['preTransformation'],
              })
              transformation = {
                ...transformation,
                pre: globalParameters['preTransformation'],
              }
            }
            if (globalParameters['postTransformation'] && globalParameters['postTransformation'].length > 0) {
              globalParameters['transformation'] = JSON.stringify({
                ...transformation,
                post: checkAllowedPostTransformation(
                  globalParameters['postTransformation'],
                  uppyFileObj.type,
                ),
              })
            }
          }

          if (uppyFileObj?.meta.overwriteCustomMetadata === false ||
            (uppyFileObj?.meta.overwriteCustomMetadata === true &&
            (uppyFileObj?.meta.customMetadataSelect === "cm_default" || isUndefined(uppyFileObj?.meta.customMetadataSelect )))) 
            {
            globalParameters['customMetadata'] = undefined
          }
          uppyObj.setFileMeta(uppyFileObj.id, globalParameters)


        })
        return true
      }
    })

    uppyObj.use(Webcam)
    uppyObj.use(GoogleDrive, {
      companionUrl: `${user?.dashboardServerURL}/api/companion`,
    }) // don't add trailing slash
    uppyObj.use(Dropbox, {
      companionUrl: `${user?.dashboardServerURL}/api/companion`,
    })
    // uppyObj.use(Facebook, {
    //   companionUrl: `${user?.dashboardServerURL}/api/companion`,
    // })
    // uppyObj.use(Instagram, {
    //   companionUrl: `${user?.dashboardServerURL}/api/companion`,
    // })
    uppyObj.use(Box, {
      companionUrl: `${user?.dashboardServerURL}/api/companion`,
    })
    uppyObj.use(OneDrive, {
      companionUrl: `${user?.dashboardServerURL}/api/companion`,
    })
    uppyObj.use(Unsplash, {
      companionUrl: `${user?.dashboardServerURL}/api/companion`,
    })
    uppyObj.use(Url, {
      companionUrl: `${user?.dashboardServerURL}/api/companion`,
    })
    uppyObj.use(ImageKitUppyPlugin, {
      id: 'ImageKit',
      authenticationEndpoint: `/api/clients/${user?.clientNumber}/upload/signature/v2`,
      publicKey: user?.publicKey ?? 'test',
      uploadEndpoint: `${addTrailingSlash(
        user?.uploadPath ?? '',
      )}api/v2-alpha/files/upload`,
      metaFields: [
        'useUniqueFileName',
        'tags',
        'folder',
        'isPrivateFile',
        'isPublished',
        'customCoordinates',
        'responseFields',
        'extensions',
        'webhookUrl',
        'overwriteFile',
        'overwriteAITags',
        'overwriteTags',
        'overwriteCustomMetadata',
        'customMetadata',
        'transformation'
      ],
      apiVersion: 'v2-alpha',
      limit: 10,
    })

    return uppyObj
  }, [folderPath, user, fileVersion, user, cmFields])

  useEffect(() => () => void uppy.close(), [])

  useEffect(() => {
    refetchFnRef.current = refetch
  }, [refetch])

  const updateUppyFilesFolderMetaData = useCallback(
    (uppyFiles: UppyFile[]) => {
      // update each file `folder` meta to reflect new path
      uppyFiles.forEach(uppyFileObj => {
        let newRelativePath = (uppyFileObj.meta.relativePath ?? '') as string
        if (!uppyFileObj.meta.relativePath) {
          newRelativePath = `/${uppyFileObj.name}`
        }

        const updatedFolderPath = getFolderPathForFile(
          folderPath,
          newRelativePath,
        )
        uppy.setFileMeta(uppyFileObj.id, {
          relativePath: newRelativePath,
          folder: fileVersion
            ? getFileVersionFolderPath(fileVersion.filePath)
            : updatedFolderPath,
        })
      })
    },
    [folderPath, uppy, fileVersion],
  )

  const updateUppyMetaData = useCallback(
    (uppyFiles: UppyFile[]) => {
      const defaultmetaData = convertDefaultUploadParameters(user)
      // update each file default metadata
      uppyFiles.forEach(uppyFileObj => {
        const IK_TEMP_GLOBAL_SETTINGS = {
          isPrivateFile: defaultmetaData?.isPrivateFile,
          isPublished: defaultmetaData?.isPublished,
          customMetadata: JSON.stringify(defaultmetaData?.customMetadata),
          customCoordinates: defaultmetaData?.customCoordinates,
          extensions: JSON.stringify(defaultmetaData?.extensions),
          useUniqueFileName: defaultmetaData?.useUniqueFileName,
          overwriteFile: defaultmetaData?.overwriteFile,
          overwriteAITags: defaultmetaData?.overwriteAITags,
          overwriteTags: defaultmetaData?.overwriteTags,
          overwriteCustomMetadata: defaultmetaData?.overwriteCustomMetadata,
        }
        if (
          uppyFileObj.type &&
          (fileConstraints.allowedFiles.image.includes(uppyFileObj.type) ||
            fileConstraints.allowedFiles.video.includes(uppyFileObj.type))
        ) {
          IK_TEMP_GLOBAL_SETTINGS['preTransformation'] = defaultmetaData?.preTransformation
          IK_TEMP_GLOBAL_SETTINGS['postTransformation'] = defaultmetaData?.postTransformation
        }
        
        uppy.setFileMeta(uppyFileObj.id, {IK_TEMP_GLOBAL_SETTINGS})
      })
    },
    [user, uppy, cmFields],
  )

  useEffect(() => {
    // get current uppy files
    const currentUppyFiles = uppy.getFiles()

    updateUppyFilesFolderMetaData(currentUppyFiles)
  }, [uppy, folderPath, updateUppyFilesFolderMetaData])

  const addFileToUppy = useCallback(
    (file: FileWithRelativePath, filePath: string): void => {
      try {
        uppy.addFile({
          name: file.name,
          type: file.type,
          data: file,
          source: 'FolderSelector',
          isRemote: false,
          meta: {
            relativePath: filePath || file.webkitRelativePath,
            folder: fileVersion
              ? getFileVersionFolderPath(fileVersion.filePath)
              : getFolderPathForFile(
                  folderPath,
                  filePath || file.webkitRelativePath,
                ),
          },
        })
      } catch (err) {
        // restriction fails error, will be automatically shown in opened modal
      }
    },
    [uppy, folderPath, fileVersion],
  )

  const onFilesDrop = useCallback(
    (files: FileWithRelativePath[]) => {
      if (
        (isRestrictedUser(user?.roles) &&
          (!(
            folderPermission === ACLPermissionEnum.CONTRIBUTE ||
            folderPermission === ACLPermissionEnum.MANAGE
          ) ||
            folderPath === '/')) ||
        (currentMediaCollection && folderPath === '/')
      )
        return

      if (files.length) {
        for (const fileObj of files) {
          addFileToUppy(fileObj, fileObj.webkitRelativePath)
        }
        openModal()
      }
    },
    [folderPath, user, uppy, folderPermission],
  )

  useEffect(() => {
    if (setOnFilesDropFunction) {
      setOnFilesDropFunction(() => onFilesDrop)
    }
  }, [onFilesDrop])

  const readFileFromDiskAndAddToUppy = async (
    fileEntryObj: FileSystemEntry,
  ): Promise<void | undefined> => {
    try {
      const fileObj = await getFileFromFileEntry(fileEntryObj)
      if (fileObj) {
        addFileToUppy(fileObj, fileEntryObj.fullPath)
      }
    } catch (err) {
      // error in reading file
    }
  }

  const onFileOrDirectoryDrop = useCallback(
    async (event: DragEvent) => {
      if (
        (isRestrictedUser(user?.roles) &&
          (!(
            folderPermission === ACLPermissionEnum.CONTRIBUTE ||
            folderPermission === ACLPermissionEnum.MANAGE
          ) ||
            folderPath === '/')) ||
        (currentMediaCollection && folderPath === '/')
      )
        return

      try {
        const items = await getAllFileEntries(event.dataTransfer.items)

        items.forEach(fileEntryObj => {
          void readFileFromDiskAndAddToUppy(fileEntryObj)
        })

        openModal()
      } catch (err) {
        // error in fetching entries from drop
      }
    },
    [folderPath, uppy, user, folderPermission],
  )

  useEffect(() => {
    if (setOnFileOrDirectoryDropFunction) {
      setOnFileOrDirectoryDropFunction(() => onFileOrDirectoryDrop)
    }
  }, [onFileOrDirectoryDrop])

  const onSubmit = useCallback(values => {
    if (Object.keys(values)?.length) {
      if (values?.name) uppy.setFileMeta(values.id, { name: values?.name })
      if (values?.tags && Array.isArray(values.tags) && values.tags.length)
        uppy.setFileMeta(values.id, {
          tags: values?.tags?.map(data => data.label).join(','),
        })
      else uppy.setFileMeta(values.id, { tags: null })
      if (
        values &&
        Object.keys(values).includes('customMetadata') &&
        Object.keys(values.customMetadata).length
      ) {
        uppy.setFileMeta(values.id, {
          customMetadata: JSON.stringify(JSON.parse(values.customMetadata)),
        })
      }
      else
      uppy.setFileMeta(values.id, {
        customMetadata: null,
      })

      if (
        values &&
        values?.customCoordinatesOb?.['x-coordinate'] &&
        values?.customCoordinatesOb?.['y-coordinate'] &&
        values?.customCoordinatesOb?.height &&
        values?.customCoordinatesOb.width
      )
        uppy.setFileMeta(values.id, {
          customCoordinates: `${values?.customCoordinatesOb['x-coordinate']},${values?.customCoordinatesOb['y-coordinate']},${values?.customCoordinatesOb.width},${values?.customCoordinatesOb?.height}`,
        })
      else
      uppy.setFileMeta(values.id, {
        customCoordinates: null,
      })

      if (values && values?.extensionsData) {
        let tempExt: Extensions[] = []
        if (values?.extensionsData?.aiTag) {
          tempExt = [...values?.aiTagData]
        }
        if (values?.extensionsData?.removeBg) {
          const options = {
            add_shadow: values?.removeBgData?.shadowCheck,
            semitransparency: values?.removeBgData?.semiTransparentCheck,
          }
          if (
            values?.removeBgData?.background === 'background_color' &&
            values?.removeBgData?.bgColor
          )
            options['bg_color'] = values?.removeBgData?.bgColor
          if (
            values?.removeBgData?.background === 'background_image' &&
            values?.removeBgData?.bgImageURL
          )
            options['bg_image_url'] = values?.removeBgData?.bgImageURL
          tempExt.push({
            name: 'remove-bg',
            options,
          })
        }
        if (tempExt?.length)
          uppy.setFileMeta(values.id, { extensions: JSON.stringify(tempExt) })
        else {
          const file = uppy.getState().files[values.id]
          uppy.setFileMeta(values.id, {...file.meta, extensions: null })
        }
      }

      if (values && values?.webhookUrl)
        uppy.setFileMeta(values.id, { webhookUrl: values?.webhookUrl })
      else
        uppy.setFileMeta(values.id, { webhookUrl: null })
      if (values && Object.keys(values).includes('useUniqueFileName'))
        uppy.setFileMeta(values.id, {
          useUniqueFileName: values?.useUniqueFileName,
        })
      else
        uppy.setFileMeta(values.id, {
          useUniqueFileName: false,
        })
      if (values && Object.keys(values).includes('overwriteFile'))
        uppy.setFileMeta(values.id, {
          overwriteFile: values?.overwriteFile,
        })
        else
        uppy.setFileMeta(values.id, {
          overwriteFile: false,
        })
      if (values && Object.keys(values).includes('isPrivateFile'))
        uppy.setFileMeta(values.id, { isPrivateFile: values?.isPrivateFile })
        else
        uppy.setFileMeta(values.id, {
          isPrivateFile: false,
        })
      if (values && Object.keys(values).includes('isPublished')) {
        uppy.setFileMeta(values.id, { isPublished: values?.isPublished })
      } else {
        uppy.setFileMeta(values.id, {
          isPublished: true,
        })
      }
      if (values && Object.keys(values).includes('overwriteAITags'))
        uppy.setFileMeta(values.id, {
          overwriteAITags: values?.overwriteAITags,
        })
        else
        uppy.setFileMeta(values.id, {
          overwriteAITags: false,
        })
      if (values && Object.keys(values).includes('overwriteTags'))
        uppy.setFileMeta(values.id, { overwriteTags: values?.overwriteTags })
        else
        uppy.setFileMeta(values.id, {
          overwriteTags: false,
        })
      if (values && Object.keys(values).includes('overwriteCustomMetadata'))
        uppy.setFileMeta(values.id, {
          overwriteCustomMetadata: values?.overwriteCustomMetadata,
        })
        else
        uppy.setFileMeta(values.id, {
          overwriteCustomMetadata: false,
        })
      if (values?.preTransformation) {
        uppy.setFileMeta(values.id, {
          preTransformation: values?.preTransformation,
        })
      } else {
        uppy.setFileMeta(values.id, { preTransformation: null })
      }
      if (values?.postTransformation?.length) {
        uppy.setFileMeta(values.id, {
          postTransformation: values?.postTransformation,
        })
      } else {
        uppy.setFileMeta(values.id, { postTransformation: null })
      }

      if (values?.customMetadataSelect) {
        uppy.setFileMeta(values.id, {
          customMetadataSelect: values?.customMetadataSelect,
        })
      }
      uppy.emit('dashboard:modal-closed')
      return
    }
  }, [uppy])

  const formSubmit = useCallback(
    event => {
      event.preventDefault()
      if (formRef) formRef?.current?.handleSubmit()
    },
    [formRef],
  )

  const onAdvacedSubmit = useCallback(
    (values:CommonSettingProps) => {
      setCommonSettings(values)
      setIsOpen(false)
      const currentUppyFiles = uppy.getFiles()
      currentUppyFiles.forEach((file: UppyFileType) => {
        file.meta.IK_TEMP_COMMON_SETTINGS=values;
      })
    },
    [setCommonSettings, setIsOpen, uppy],
  )

  useEffect(() => {
    if (isModalOpen && commonSettings) {
      const currentUppyFiles = uppy.getFiles()
      currentUppyFiles.forEach((file: UppyFileType) => {
        file.meta.IK_TEMP_COMMON_SETTINGS = commonSettings
      })
    }
  }, [isModalOpen, uppy, commonSettings])


  useEffect(() => {
    //@ts-ignore
    uppy.off('dashboard:file-edit-start', () => {})
    if (isModalOpen && formRef && onSubmit) {
      //@ts-ignore
      uppy.on('dashboard:file-edit-start', file => {
        const insertedElement = document.getElementById('unique') as HTMLElement
        if (insertedElement) {
          insertedElement.style.display = 'block'
        } else {
          const flagElement = document.querySelector(
            '.uppy-DashboardContent-bar',
          ) as HTMLElement
          if (flagElement) {
            const div = document.createElement('div')
            div.id = 'unique'
            div.style.height = '700px'
            div.style.zIndex = '9999'
            div.style.background = '#fff'
            div.style.position = 'absolute'
            div.style.top = '0'
            div.style.width = '100%'
            flagElement.parentNode?.insertBefore(div, flagElement.nextSibling)
          }
        }

        setTimeout(() => {
          const filePreview = document.querySelector(
            '.uppy-Dashboard-FileCard-preview',
          ) as HTMLElement
          if (filePreview) filePreview.style.display = 'none'
          const fileDialog = document.getElementById('edit-dialog')
          if (!fileDialog) {
            const fileInfo = document.querySelector(
              '.uppy-Dashboard-FileCard-info',
            ) as HTMLElement
            if (fileInfo) fileInfo.style.display = 'none'
            const statusBar = document.querySelector(
              '.uppy-Dashboard-FileCard-actions',
            ) as HTMLElement
            const div = document.createElement('div')
            div.id = 'edit-dialog'
            div.style.height = '100%'
            div.style.padding = '30px 20px 20px 20px'
            div.style.overflowY = 'auto'
            fileInfo.parentNode?.insertBefore(div, statusBar)
            ReactDOM.render(
              <EditMetadata
                file={{
                  ...file?.meta,
                  id: file?.id,
                  preview: file?.preview,
                  type: file?.type
                }}
                formRef={formRef}
                onSubmit={onSubmit}
                cmFields={cmFields}
                fileVersion={fileVersion}
              />,
              div,
            )
            const saveChanges = document.querySelector(
              '.uppy-Dashboard-FileCard-actionsBtn',
            ) as HTMLElement
            if (saveChanges) {
              saveChanges.addEventListener('click', formSubmit)
            }
          }
        }, 0)
      })
    }

    // uppy.on('progress', (progress) => {
    // 	dispatch({ type : 'TOGGLE_UPLOAD_PERCENTAGE', payload : progress });
    // })
    // uppy.on('complete', (result) => {
    // 	let percent;
    // 	if(!result.successful.length) {
    // 		percent = -1;
    // 	}else{
    // 		if(!result.failed.length){
    // 			percent = 100;
    // 		}else{
    // 			percent = 101;
    // 		}
    // 	}

    // 	dispatch({ type : 'TOGGLE_UPLOAD_PERCENTAGE', payload : percent });
    // })

    // uppy.on('error', (err) => { // no need to send sentry error as `upload-error` sends it on every file upload
    // 	dispatch({ type : 'TOGGLE_UPLOAD_PERCENTAGE', payload : -1 });
    // })

    // uppy.on('upload-error', (file, err) => {
    // 	Sentry.captureException(err.stack);
    // })

    // uppy.on('restriction-failed', (file, err) => {
    // 	Sentry.captureMessage(`Uppy file addition restricted for file name - ${file.name} and type - ${file.type}`);
    // })
    return ()=>{
      const saveChanges = document.querySelector('.uppy-Dashboard-FileCard-actionsBtn') as HTMLElement
      if (saveChanges) {
        saveChanges.removeEventListener('click', ()=>{})
      }
    }
  }, [isModalOpen, formRef, onSubmit, fileVersion])

  const onFileAdded = useCallback(
    files => {
      updateUppyFilesFolderMetaData(files)
      updateUppyMetaData(files)
      if (files && Array.isArray(files) && files?.length) {
        const commonSettingsButton = document.getElementById('upload-button')
        if (commonSettingsButton)
          commonSettingsButton.style.visibility = 'visible'
      }
      const currentUppyFiles = uppy.getFiles()
      currentUppyFiles.forEach((file: UppyFileType) => {
        file.meta.IK_TEMP_COMMON_SETTINGS = commonSettings
      })
    },
    [updateUppyFilesFolderMetaData, uppy, updateUppyMetaData, commonSettings],
  )

  useEffect(() => {
    uppy.on('files-added', files => onFileAdded(files))
    return () => {
      uppy.off('files-added', () => onFileAdded)
    }
  }, [onFileAdded])

  useEffect(() => {
    uppy.on('file-removed', () => {
      let filesToUpload = 0
      const currentUppyFiles = uppy.getFiles()
      currentUppyFiles.forEach((file: UppyFileType) => {
        if (!file?.progress?.uploadComplete) filesToUpload = filesToUpload + 1
      })
      const commonSettingsButton = document.getElementById('upload-button')
      if (commonSettingsButton && !filesToUpload)
        commonSettingsButton.style.visibility = 'hidden'
    })
    return () => {
      uppy.off('file-removed', () => {})
    }
  }, [uppy])

  const onUploadSuccess = useCallback(() => {
    window.trackEvent({ label: 'UploadedFile' })
      shouldRefetch.current = true
      if (fileVersion) {
        void refetchFnRef.current()
        shouldRefetch.current = false
        closeModal()
        uppy.cancelAll()
        toast({
          description: t(
            'file_version_successfully_added',
            'A new file version was successfully uploaded. Select the latest version of the file to view its details.',
          ),
          status: 'success',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
      } 
  }, [folderPath, uppy, fileVersion])

  useEffect(() => {
    uppy.on('upload-success', onUploadSuccess)
    return () => {
      uppy.off('upload-success', onUploadSuccess)
    }
  }, [folderPath, uppy])

  useEffect(() => {
    if (isModalOpen && onAdvacedSubmit) {
      const previous = document.getElementById('upload-button')
      if (!previous && isModalOpen && !fileVersion) {
        // get the html element with class name uppy-StatusBar-actions
        const statusBarAction = document.querySelector(
          '.uppy-StatusBar-actions',
        ) as HTMLElement
        statusBarAction.style.display = 'flex'
        statusBarAction.style.justifyContent = 'space-between'
        const uploadButton = document.querySelector(
          '.uppy-StatusBar-actions',
        ) as HTMLElement

        // dynamically create a button element and insert it into the element with '.uppy-StatusBar-actions' class
        const button = document.createElement('button')
        button.id = 'upload-button'
        button.innerHTML = 'Common settings'
        button.classList.add('uppy-DashboardContent-back')

        // Add a custom attribute to the button element
        button.setAttribute('data-uppy-super-focusable', 'true')
        button.addEventListener('click', () => {
          setIsOpen(true)
        })
        uploadButton?.appendChild(button)
      }
      const dashboardContainer = document.querySelector(
        '.uppy-Dashboard-inner',
      ) as HTMLElement
      if (dashboardContainer) {
        dashboardContainer.style.zIndex = '2100'
      }
      if (window.matchMedia('(min-width: 820px)')) {
        dashboardContainer.style.width = '900px'
        dashboardContainer.style.height = '700px'
      }

      ReactDOM.render(
        <ChakraProvider theme={extendTheme(theme)}>
          <UserProvider>
            <AdvancePopup
              isOpen={isOpen}
              onClose={() => setIsOpen(false)}
              onSubmit={onAdvacedSubmit}
              commonSettings={commonSettings}
              cmFields={cmFields}
            />
          </UserProvider>
        </ChakraProvider>,
        dashboardContainer,
      )
    }
    return () => {
      const commonSettingsButton = document.getElementById('upload-button')
      if (commonSettingsButton)
        commonSettingsButton.removeEventListener('click', () => {})
    }
  }, [isModalOpen, isOpen, onAdvacedSubmit, commonSettings, fileVersion])

  useEffect(() => {
    const handlePaste = (event: CustomFileEvent): void => {
      const { file } = event.detail
      if (file) {
        if (!isModalOpen) {
          openModal()
        }

        setTimeout(() => {
          addFileToUppy(file, file.webkitRelativePath)
        }, 100);
      }
    }
  
    // Add a listener to CustomEvent "ik-file-pasted" to handle pasted files
    window.addEventListener('ik-file-pasted', handlePaste as EventListener)
  
    return () => {
      window.removeEventListener('ik-file-pasted', handlePaste as EventListener)
    }
  }, [uppy, addFileToUppy, openModal, isModalOpen])

  return (
    <Portal>
      <DashboardModal
        uppy={uppy}
        closeModalOnClickOutside={false}
        open={isModalOpen}
        onRequestClose={() => {
          if (shouldRefetch.current) {
            void refetchFnRef.current()
            shouldRefetch.current = false
          }
          closeModal()
        }}
        // autoProceed={false}
        // allowMultipleUploads={true}
        showLinkToFileUploadResult={true}
        proudlyDisplayPoweredByUppy={false}
        showProgressDetails={true}
        doneButtonHandler={() => {
          uppy.cancelAll()
          if (shouldRefetch.current) {
            void refetchFnRef.current()
            shouldRefetch.current = false
          }
          closeModal()
          setCommonSettings({})
        }}
        plugins={[
          'Webcam',
          'GoogleDrive',
          'Dropbox',
          // 'Facebook',
          // 'Instagram',
          'Url',
          'Box',
          'OneDrive',
          'Unsplash',
        ]}
        metaFields={
          fileVersion
            ? metaFields.filter(metaObject =>
                ['isPrivateFile', 'isPublished', 'tags', 'customCoordinates'].includes(
                  metaObject.id,
                ),
              )
            : metaFields
        }
      />
    </Portal>
  )
}

export default FileUpload
